import React from "react";
import { Accordion, Card, Container, Row } from "react-bootstrap";
import { Link } from "react-router-dom";

import { PageOverLay, SEO } from "../Components";
import { pathTitles, useStore } from "../Store/Store";

const numbersIn = (programs) =>
  (programs || [])
    .map((p) => parseInt(String(p.cost).replace(/[^0-9]/g, ""), 10))
    .filter((n) => !Number.isNaN(n));

const minCost = (programs) => {
  const nums = numbersIn(programs);
  return nums.length ? Math.min(...nums) : null;
};

const maxCost = (programs) => {
  const nums = numbersIn(programs);
  return nums.length ? Math.max(...nums) : null;
};

const fmt = (n) => (n == null ? "" : `$${n.toLocaleString("en-US")}`);

export const FAQ = () => {
  const getLink = useStore((state) => state.getLink);
  const inHomePrograms = useStore((state) => state.inHomePrograms);
  const dayTrainingPrograms = useStore((state) => state.dayTrainingPrograms);
  const btAdultPrograms = useStore((state) => state.btAdultPrograms);
  const btPuppyPrograms = useStore((state) => state.btPuppyPrograms);

  const privateMin = fmt(minCost(inHomePrograms));
  const dayMin = fmt(minCost(dayTrainingPrograms));
  const btAll = [...(btAdultPrograms || []), ...(btPuppyPrograms || [])];
  const btMin = fmt(minCost(btAll));
  const btMax = fmt(maxCost(btAll));

  const btPuppyPath = getLink(pathTitles.BOARDANDTRAINPUPPY);
  const batpPath = getLink(pathTitles.BUYATRAINEDPUPPY);

  const faqs = [
    {
      q: "What areas does Todo's Translator serve for dog training?",
      text: "We provide in-home dog training across Indianapolis and the surrounding communities — including Carmel, Noblesville, Fishers, Westfield, and Zionsville.",
    },
    {
      q: "Do you offer in-home dog training, or do I need to travel to a facility?",
      text: "All private lessons are conducted in your home, where your dog is most comfortable and where behavior issues actually happen. Day Training is conducted in your home or in public locations depending on your dog's needs. Board & Train programs are conducted at our facility.",
    },
    {
      q: "How much does professional dog training cost?",
      text: `Pricing depends on the program. Private lessons start at ${privateMin}, Day Training starts at ${dayMin}, and our Board & Train programs range from ${btMin} to ${btMax}. Every program includes follow-up support to ensure lasting results.`,
    },
    {
      q: "What is the difference between Day Training and Board & Train?",
      text: "With Day Training, Justine comes to you — she works with your dog in your home or out in public, for 2–5 hours per session depending on your dog's focus, endurance, and goals. Board & Train is a multi-week, full-immersion program at our facility — ideal for faster results, more serious behavior work, and lasting change.",
    },
    {
      q: "Can you train my puppy?",
      jsx: (
        <>
          Absolutely. Our <Link to={btPuppyPath}>Board &amp; Train Puppy</Link>{" "}
          and <Link to={batpPath}>Buy A Trained Puppy</Link> programs are
          designed specifically for young dogs, building a foundation of
          obedience, confidence, and good manners.
        </>
      ),
      text: `Absolutely. Our Board & Train Puppy (${btPuppyPath}) and Buy A Trained Puppy (${batpPath}) programs are designed specifically for young dogs, building a foundation of obedience, confidence, and good manners.`,
    },
    {
      q: "What training methods do you use?",
      text: "We use balanced, positive, relationship-based training. Every dog is different, so we tailor the approach — but our focus is always on clear communication, consistency, and building trust between you and your dog.",
    },
    {
      q: "How long does it take to see results?",
      text: "Most owners see noticeable improvement within the first few sessions. Full behavior change — especially for reactivity, aggression, or severe anxiety — typically takes 4–8 weeks of consistent work.",
    },
    {
      q: "What behaviors can you address?",
      text: "Leash reactivity, aggression, fear and anxiety, recall, loose-leash walking, door manners, jumping, mouthing, resource guarding, housebreaking, and general obedience.",
    },
    {
      q: "Do I need to be present during training?",
      text: "For private lessons, yes — training the handler is as important as training the dog. For Board & Train, we handle the dog's development and then transfer the skills to you in turnover sessions.",
    },
    {
      q: "How do I get started?",
      jsx: (
        <>
          Fill out the{" "}
          <Link to={getLink(pathTitles.CONTACTUS)}>contact form</Link> on this
          site or text <a href="sms://+13179546952">(317) 954-6952</a>. Justine
          will reach out to set up a free phone consultation to discuss your dog
          and recommend the right program.
        </>
      ),
      text: "Fill out the contact form on this site or text (317) 954-6952. Justine will reach out to set up a free phone consultation to discuss your dog and recommend the right program.",
    },
  ];

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map(({ q, text }) => ({
      "@type": "Question",
      name: q,
      acceptedAnswer: { "@type": "Answer", text },
    })),
  };

  const overlay = {
    where: "trainingAll",
    title: "Frequently Asked Questions",
    caption: "Everything you need to know before getting started.",
  };
  const info = {
    routes: { Home: "/", FAQ: getLink(pathTitles.FAQ) },
  };

  return (
    <>
      <SEO
        title="Dog Training FAQ"
        description="Common questions about Todo's Translator dog training — pricing, programs, methods, and how to get started. Serving Indianapolis and surrounding areas."
        path="/faq"
        jsonLd={jsonLd}
      />
      <PageOverLay overlay={overlay} />
      <Container className="small_container common_container faq_container">
        <Row>
          <Card.Header className="common_header">
            {Object.keys(info.routes).map((routeName, i) => (
              <React.Fragment key={`${info.routes[routeName]}_${i}`}>
                <Link to={info.routes[routeName]}>{routeName}</Link>
                {i < Object.keys(info.routes).length - 1 && <>&nbsp;/&nbsp; </>}
              </React.Fragment>
            ))}
          </Card.Header>
        </Row>
        <Row className="faq_row">
          <Card.Title as="h2" className="faq_heading">
            Frequently Asked Questions
          </Card.Title>
          <Accordion className="faq_accordion" alwaysOpen>
            {faqs.map((faq, i) => (
              <Accordion.Item eventKey={String(i)} key={i} className="faq_item">
                <Accordion.Header as="h3">{faq.q}</Accordion.Header>
                <Accordion.Body>{faq.jsx || faq.text}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Row>
      </Container>
    </>
  );
};
